import React, { useState, useEffect, useMemo } from 'react';
import { format, subMonths, subYears, isAfter } from 'date-fns';
import {
  ResponsiveContainer, AreaChart, LineChart, Area, Line,
  XAxis, YAxis, Tooltip, ReferenceLine, Legend,
} from 'recharts';
import {
  Percent, TrendingUp, TrendingDown, AlertTriangle, ChevronDown, ChevronUp,
  Activity, Bitcoin, Coins, Home, BarChart3, Sparkles, CreditCard, Car,
} from 'lucide-react';
import useMarketData from '../hooks/useMarketData.js';

/**
 * BondYieldCard
 * -------------
 * Interactive bond-yield panel. Replaces the original "wall of explainer
 * text" approach with three visual / interactive widgets that teach the
 * relationships through play rather than reading:
 *
 *  1. YieldZoneMeter  — always-visible band showing where today's 10Y sits
 *     relative to historical regimes (Easy / Neutral / Tight / Restrictive)
 *
 *  2. YieldImpactSimulator — slider: drag the 10Y from 0% to 8% and watch
 *     a $400K mortgage payment, BTC's "competing risk-free return", and a
 *     stylized equity-multiple pressure number recompute live
 *
 *  3. YieldVsAssetOverlay — toggle BTC / gold against monthly DGS10 closes
 *     back to 1962, normalised so the inverse / lead-lag relationship is
 *     visible at a glance
 *
 * The original text explainer collapses to a tiny "definitions" footnote.
 *
 * Subscribes to useMarketData — no parallel FRED fetch.
 */
export default function BondYieldCard() {
  const { fred, btc, gold, equity } = useMarketData();
  const [defsOpen, setDefsOpen] = useState(false);

  const dgs10  = fred?.dgs10  ?? [];
  const t10y2y = fred?.t10y2y ?? [];

  // Walk back through trailing nulls — daily series can publish at staggered
  // times (T10Y2Y ahead of DGS10) so always grab last valid.
  const latest = useMemo(() => {
    const last10 = lastValid(dgs10);
    const last2y = lastValid(t10y2y);
    if (!last10 || !last2y) return null;

    const cutoff = subMonths(last10.date, 1);
    const monthAgo10 = findOnOrBefore(dgs10, cutoff);
    const delta30 = monthAgo10 ? last10.value - monthAgo10.value : null;

    return {
      yield10: last10.value,
      yield10Date: last10.date,
      spread: last2y.value,
      spreadDate: last2y.date,
      inverted: last2y.value < 0,
      delta30,
    };
  }, [dgs10, t10y2y]);

  // Recent (12-month) sparkline for the small chart up top
  const recentSparkline = useMemo(() => {
    if (!dgs10.length) return [];
    const cutoff = subMonths(new Date(), 12);
    return dgs10
      .filter(o => o.date >= cutoff && Number.isFinite(o.value))
      .map(o => ({ ts: o.date.getTime(), value: o.value }));
  }, [dgs10]);

  // Loading / error states
  if (fred?.status === 'loading' && !latest) return <Skeleton />;
  if (!latest) {
    return (
      <section className="rounded-2xl border border-white/10 bg-slate-900/60 p-4 md:p-6">
        <div className="flex items-center gap-2 text-slate-400">
          <AlertTriangle className="w-4 h-4" />
          <span className="text-sm">Bond-yield data unavailable</span>
        </div>
      </section>
    );
  }

  const { yield10, yield10Date, spread, spreadDate, inverted, delta30 } = latest;
  const trend = delta30 == null ? null : delta30 >= 0 ? 'up' : 'down';

  return (
    <section className="rounded-2xl border border-white/10 bg-gradient-to-br from-sky-500/[0.03] via-slate-900/60 to-blue-500/[0.03] backdrop-blur-xl p-4 md:p-6 space-y-6">
      {/* ── Header ───────────────────────────────────────────── */}
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-lg font-semibold text-slate-100 flex items-center gap-2">
            <Percent className="w-5 h-5 text-sky-400" />
            US Treasury Bond Yields
          </h2>
          <p className="text-xs text-slate-400 mt-0.5">
            The interest rate the market demands to lend the US government money — the
            most-watched number in finance
          </p>
        </div>
        <div className="text-right">
          <div className="text-[10px] uppercase tracking-widest text-slate-500">As of</div>
          <div className="text-xs text-slate-300 font-mono tabular-nums">
            {format(yield10Date, 'MMM d, yyyy')}
          </div>
          {/* FRED publishes Treasury yields with a 1-2 business-day lag from
              the H.15 release. Without this hint, users on a Tuesday seeing
              a Friday timestamp wonder whether the dashboard is broken. */}
          <div
            className="text-[9px] text-slate-500 mt-0.5"
            title="FRED publishes Treasury yields with a 1-2 business-day lag — this is the freshest data available."
          >
            FRED · 1-2 business-day lag
          </div>
        </div>
      </div>

      {/* ── Top tiles ────────────────────────────────────────── */}
      {/*
        Date labels are deliberately consistent across both tiles and the
        panel header — same "MMM d, yyyy" format. Reviewer flagged that
        mixing "May 18" and "May 15, 2026" was confusing; readers couldn't
        tell which was the canonical "as of" date.
      */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <YieldTile
          label="10-Year Treasury"
          subtitle="DGS10 — benchmark long rate"
          value={`${yield10.toFixed(2)}%`}
          trend={trend}
          delta={delta30}
          color="#38bdf8"
          asOf={yield10Date}
        />
        <SpreadTile
          label="10Y - 2Y Curve Spread"
          subtitle="T10Y2Y — recession indicator"
          value={`${spread >= 0 ? '+' : ''}${spread.toFixed(2)}%`}
          inverted={inverted}
          asOf={spreadDate}
        />
      </div>

      {recentSparkline.length > 30 && <RecentTrendChart data={recentSparkline} latest={yield10} />}

      {inverted && <InversionBanner />}

      {/* ── 1. Where are we? Zone meter ──────────────────────── */}
      <YieldZoneMeter yield10={yield10} dgs10={dgs10} />

      {/* ── 2. What changes if yields move? Impact simulator ─── */}
      <YieldImpactSimulator
        currentYield={yield10}
        btcPrice={btc?.spot}
        actualSp500Pe={equity?.sp500Pe}
      />

      {/* ── 3. Empirical: yields vs BTC / gold ──────────────── */}
      <YieldVsAssetOverlay dgs10={dgs10} btcHistory={btc?.history} goldHistory={gold?.history} />

      {/* ── Definitions footnote (collapsed by default) ──────── */}
      <button
        onClick={() => setDefsOpen(d => !d)}
        className="w-full flex items-center justify-between gap-2 px-3 py-2 rounded-lg border border-white/5 bg-slate-900/30 hover:bg-slate-900/50 transition-colors text-left text-xs text-slate-400"
      >
        <span>Definitions: bond yield, curve inversion, real-world impact</span>
        {defsOpen ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
      </button>
      {defsOpen && <DefinitionsFootnote />}

      <p className="text-[11px] text-slate-500 italic">
        Source: FRED via the Federal Reserve Bank of St. Louis — series IDs DGS10
        (10-Year Treasury, daily, since 1962) and T10Y2Y (10Y-2Y constant-maturity
        spread, since 1976). Updated daily on business days.
      </p>
    </section>
  );
}

// ─── 1. YieldZoneMeter ──────────────────────────────────────────────────────
//
// Visual answer to "where are we right now?". Builds five named zones from
// long-run distribution percentiles of the post-Volcker-era 10Y series and
// shows a marker at today's value. The percentile-based bands make this
// honest — labels like "Easy / Neutral / Tight" are anchored to actual
// historical experience, not made-up thresholds.

function YieldZoneMeter({ yield10, dgs10 }) {
  // Use post-1990 data (post-Volcker normalization, modern monetary regime)
  // for the percentile distribution. Pre-1990 yields hit double digits and
  // would skew the "Restrictive" threshold unhelpfully high.
  const stats = useMemo(() => {
    const cutoff = new Date('1990-01-01');
    const vals = dgs10
      .filter(o => o.date >= cutoff && Number.isFinite(o.value))
      .map(o => o.value)
      .sort((a, b) => a - b);
    if (vals.length < 100) return null;
    return {
      p10: vals[Math.floor(vals.length * 0.10)],
      p33: vals[Math.floor(vals.length * 0.33)],
      p67: vals[Math.floor(vals.length * 0.67)],
      p90: vals[Math.floor(vals.length * 0.90)],
      min: vals[0],
      max: vals[vals.length - 1],
    };
  }, [dgs10]);

  if (!stats) {
    return null;
  }

  // Scale: clamp the visual to [0%, 8%] — captures 95%+ of post-1990 obs
  const SCALE_MIN = 0;
  const SCALE_MAX = 8;
  const pct = (v) => Math.max(0, Math.min(100, ((v - SCALE_MIN) / (SCALE_MAX - SCALE_MIN)) * 100));
  const zones = [
    { name: 'Easy money',      color: '#34d399', from: SCALE_MIN, to: stats.p10, hint: 'Recession / QE era' },
    { name: 'Below normal',    color: '#a3e635', from: stats.p10, to: stats.p33, hint: 'Accommodative' },
    { name: 'Neutral',         color: '#facc15', from: stats.p33, to: stats.p67, hint: 'Long-run average' },
    { name: 'Tight',           color: '#fb923c', from: stats.p67, to: stats.p90, hint: 'Restrictive territory' },
    { name: 'Highly restrictive', color: '#f87171', from: stats.p90, to: SCALE_MAX, hint: 'Crisis / shock zone' },
  ];

  const currentZone = zones.find(z => yield10 >= z.from && yield10 < z.to)
                   ?? zones[zones.length - 1];

  return (
    <div className="rounded-xl border border-white/10 bg-slate-900/40 p-3 md:p-5">
      <div className="flex items-start justify-between gap-3 mb-3 flex-wrap">
        <div>
          <div className="text-xs font-semibold uppercase tracking-widest text-slate-300 flex items-center gap-2">
            <Activity className="w-3.5 h-3.5 text-sky-400" />
            Where are we right now?
          </div>
          <p className="text-[11px] text-slate-500 mt-0.5">
            Today's 10Y yield mapped onto historical regime zones (post-1990 distribution)
          </p>
        </div>
        <div className="text-right">
          <div className="text-[10px] uppercase tracking-widest text-slate-500">Current zone</div>
          <div className="text-sm font-bold tabular-nums" style={{ color: currentZone.color }}>
            {currentZone.name}
          </div>
        </div>
      </div>

      {/* Zone bar */}
      <div className="relative h-12">
        <div className="absolute inset-0 flex rounded-md overflow-hidden">
          {zones.map(z => (
            <div
              key={z.name}
              className="h-full"
              style={{
                background: z.color,
                width: `${pct(z.to) - pct(z.from)}%`,
                opacity: z.name === currentZone.name ? 0.85 : 0.35,
                transition: 'opacity 0.4s',
              }}
              title={`${z.name}: ${z.from.toFixed(2)}% – ${z.to.toFixed(2)}%`}
            />
          ))}
        </div>

        {/* Marker for today's yield */}
        <div
          className="absolute top-0 h-full w-0.5 bg-white shadow-[0_0_12px_rgba(255,255,255,0.8)]"
          style={{ left: `${pct(yield10)}%`, transform: 'translateX(-50%)', transition: 'left 0.6s ease-out' }}
        >
          <div className="absolute -top-7 left-1/2 -translate-x-1/2 whitespace-nowrap bg-white text-slate-900 text-[10px] font-bold px-2 py-0.5 rounded-md tabular-nums shadow-lg">
            {yield10.toFixed(2)}%
          </div>
        </div>
      </div>

      {/* Scale ticks */}
      <div className="relative h-4 mt-1">
        {[0, 2, 4, 6, 8].map(t => (
          <div
            key={t}
            className="absolute text-[9px] font-mono text-slate-500 -translate-x-1/2"
            style={{ left: `${pct(t)}%` }}
          >
            {t}%
          </div>
        ))}
      </div>

      {/* Zone legend — name + subdescriptor stacked vertically per item so
          full text stays readable across viewport widths instead of getting
          truncated to "Long-run avera…". 11px text (up from 10px) for the
          name; subdescriptor stays at 10px in slate-500 for hierarchy. */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-x-3 gap-y-2 mt-3">
        {zones.map(z => (
          <div
            key={z.name}
            className="flex items-start gap-2"
            style={{ opacity: z.name === currentZone.name ? 1 : 0.55 }}
          >
            <span className="w-2 h-2 rounded-full mt-1 flex-shrink-0" style={{ background: z.color }} />
            <div className="min-w-0 leading-tight">
              <div className="text-[11px] text-slate-200 font-semibold">{z.name}</div>
              <div className="text-[10px] text-slate-500 mt-0.5">{z.hint}</div>
              <div className="text-[9px] text-slate-600 font-mono tabular-nums mt-0.5">
                {z.from.toFixed(1)}-{z.to.toFixed(1)}%
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── 2. YieldImpactSimulator ────────────────────────────────────────────────
//
// Slider that turns abstract percentages into concrete dollar amounts. Three
// translations:
//
//   - 30-year mortgage payment on a $400K home loan
//     Mortgage rate ≈ 10Y + 200 bps historical spread (rough approximation)
//
//   - "Risk-free competing return" on $10K parked in 10Y Treasuries
//     (annualised yield × $10K — vs. holding BTC for the same period)
//
//   - Equity P/E compression — the "fair P/E" for the S&P 500 as a function
//     of yields. Stylized formula: P/E ≈ 1 / (yield + equity risk premium).
//     With ERP held constant at 4%, P/E moves inversely with yields.
//
// All numbers are clearly labelled as educational approximations — they're
// not financial-planning grade.

function YieldImpactSimulator({ currentYield, btcPrice, actualSp500Pe }) {
  const [simulatedYield, setSimulatedYield] = useState(currentYield);

  // Snap to today's value when underlying data updates
  useEffect(() => { setSimulatedYield(currentYield); }, [currentYield]);

  // ─── Borrowing cost models ─────────────────────────────────────────────
  // All three are educational approximations — they project how the slider's
  // 10Y yield would propagate into common consumer rates. They are NOT live
  // APR feeds. The chain of assumptions is documented in the disclaimer.

  // 1. Mortgage — 30-year fixed on $400K. Mortgage rate ≈ 10Y + 2pp
  // (historical average spread; actual MBS spread fluctuates 1.5-2.5pp).
  const mortgageRate = simulatedYield + 2.0;
  const monthlyMortgage = mortgagePayment(400_000, mortgageRate, 30);

  // 2. Auto loan — 60-month new auto on $40K. Auto APR tracks closer to
  // the 5Y than the 10Y, but for a single-slider simulator we proxy the
  // 5Y ≈ 10Y - 0.5pp historical, then add the typical 4.5pp lender margin.
  // Net effect: auto APR ≈ 10Y + 4.0pp. This sits between FRED's
  // TERMCBAUTO48NS (which historically runs 10Y + 4-5pp) and current
  // promotional pricing on prime credit. Don't treat it as a quote.
  const autoRate = simulatedYield + 4.0;
  const monthlyAuto = mortgagePayment(40_000, autoRate, 5);

  // 3. Credit card — average APR on a $5K balance. CC APR tracks the Prime
  // Rate (which moves with the FFR, not directly with the 10Y), plus a
  // ~18pp issuer margin for mid-tier consumer cards. The assumption that
  // FFR ≈ 10Y - 1pp on average lets us drive everything from one slider:
  //   Prime ≈ FFR + 3pp ≈ (10Y - 1pp) + 3pp = 10Y + 2pp
  //   CC APR ≈ Prime + 18pp ≈ 10Y + 20pp
  // This is the loosest of the three approximations — credit-card APRs are
  // famously sticky and don't move 1:1 with policy rates. Useful for
  // "yields go up → you pay more" intuition, NOT for budget planning.
  const creditCardRate = simulatedYield + 20.0;
  const monthlyMinPayment = creditCardMinPayment(5_000, creditCardRate);

  // 4. Treasury "competing return" on $10K → annualised dollars
  const treasuryAnnual = 10_000 * (simulatedYield / 100);

  // 5. Stylized fair P/E (constant 4% ERP)
  const ERP = 4.0;
  const fairPE = 100 / (simulatedYield + ERP);

  // Same metrics at today's actual yield, for the delta
  const baselineMortgage   = mortgagePayment(400_000, currentYield + 2.0, 30);
  const baselineAuto       = mortgagePayment(40_000,  currentYield + 4.0, 5);
  const baselineMinPayment = creditCardMinPayment(5_000, currentYield + 20.0);
  const baselineTreasury   = 10_000 * (currentYield / 100);
  const baselineFairPE     = 100 / (currentYield + ERP);

  const moveBps = Math.round((simulatedYield - currentYield) * 100);
  const direction = moveBps > 0 ? 'up' : moveBps < 0 ? 'down' : 'flat';

  return (
    <div className="rounded-xl border border-white/10 bg-slate-900/40 p-3 md:p-5">
      <div className="flex items-start justify-between gap-3 mb-3 flex-wrap">
        <div>
          <div className="text-xs font-semibold uppercase tracking-widest text-slate-300 flex items-center gap-2">
            <Sparkles className="w-3.5 h-3.5 text-sky-400" />
            What changes if yields move?
          </div>
          <p className="text-[11px] text-slate-500 mt-0.5">
            Drag the slider to see how a different 10Y yield ripples into mortgages, savings, and equities
          </p>
        </div>
        <div className="text-right">
          <div className="text-[10px] uppercase tracking-widest text-slate-500">Simulated 10Y</div>
          <div className="text-2xl font-bold text-sky-400 tabular-nums">
            {simulatedYield.toFixed(2)}%
          </div>
          <div className={`text-[10px] font-mono tabular-nums ${
            direction === 'up' ? 'text-orange-300'
            : direction === 'down' ? 'text-emerald-300'
            : 'text-slate-500'
          }`}>
            {direction === 'flat' ? 'at today' : `${moveBps > 0 ? '+' : ''}${moveBps} bps from today`}
          </div>
        </div>
      </div>

      {/* Slider — accessible: arrow keys move ±0.05, Page Up/Down move ±0.5,
          Home/End jump to 0/8. Screen readers announce the current value
          via aria-valuenow / aria-valuetext. */}
      <div className="relative">
        <input
          type="range"
          min="0" max="8" step="0.05"
          value={simulatedYield}
          onChange={(e) => setSimulatedYield(parseFloat(e.target.value))}
          className="yield-sim-slider w-full"
          aria-label="Simulated 10-year Treasury yield"
          aria-valuemin={0}
          aria-valuemax={8}
          aria-valuenow={simulatedYield}
          aria-valuetext={`${simulatedYield.toFixed(2)} percent`}
        />
        <div className="flex justify-between mt-1 text-[9px] font-mono text-slate-500" aria-hidden="true">
          <span>0%</span><span>2%</span><span>4%</span><span>6%</span><span>8%</span>
        </div>
        {/* "Today" hash mark on the slider */}
        <div
          className="absolute top-1/2 -translate-y-1/2 w-0.5 h-3 bg-white pointer-events-none"
          style={{ left: `${(currentYield / 8) * 100}%` }}
          title={`Today: ${currentYield.toFixed(2)}%`}
        />
      </div>

      {/* Impact tiles — split into two rows so the framing is obvious:
          Row 1 = "you owe" (borrowing costs go up when yields rise);
          Row 2 = "you earn / you own" (savings + equities react differently).
          Primary values are uniformly orange across all tiles to anchor
          the visual scale; icon color carries the tile's category. */}

      <div className="mt-5">
        <div className="text-[10px] uppercase tracking-widest text-slate-400 font-bold mb-2 flex items-center gap-2">
          <span className="w-1 h-1 rounded-full bg-orange-400/60" />
          Borrowing costs — what you'd owe
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <ImpactTile
            icon={Home}
            iconColor="#fb923c"
            label="$400K mortgage · 30yr"
            headline={`$${Math.round(monthlyMortgage).toLocaleString()}/mo`}
            subtitle={`@ ${mortgageRate.toFixed(2)}% (10Y + 2pp)`}
            delta={monthlyMortgage - baselineMortgage}
            deltaUnit="$/mo"
            goodWhen="falls"
          />
          <ImpactTile
            icon={Car}
            iconColor="#f87171"
            label="$40K auto loan · 60mo"
            headline={`$${Math.round(monthlyAuto).toLocaleString()}/mo`}
            subtitle={`@ ${autoRate.toFixed(2)}% (10Y + 4pp)`}
            delta={monthlyAuto - baselineAuto}
            deltaUnit="$/mo"
            goodWhen="falls"
          />
          <ImpactTile
            icon={CreditCard}
            iconColor="#a78bfa"
            label="$5K credit card balance"
            headline={`$${Math.round(monthlyMinPayment).toLocaleString()}/mo`}
            subtitle={`@ ${creditCardRate.toFixed(2)}% APR (10Y + 20pp est.)`}
            delta={monthlyMinPayment - baselineMinPayment}
            deltaUnit="$/mo"
            goodWhen="falls"
          />
        </div>
      </div>

      <div className="mt-5">
        <div className="text-[10px] uppercase tracking-widest text-slate-400 font-bold mb-2 flex items-center gap-2">
          <span className="w-1 h-1 rounded-full bg-emerald-400/60" />
          Earning power — what your money does
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <ImpactTile
            icon={Coins}
            iconColor="#34d399"
            label="$10K in 10Y Treasury"
            headline={`$${Math.round(treasuryAnnual).toLocaleString()}/yr`}
            subtitle="Risk-free annual interest"
            delta={treasuryAnnual - baselineTreasury}
            deltaUnit="$/yr"
            goodWhen="rises"
          />
          <PETile
            fairPE={fairPE}
            actualPE={actualSp500Pe}
            deltaFromBaseline={fairPE - baselineFairPE}
          />
        </div>
      </div>

      {/* BTC connection — only when we have a live price */}
      {Number.isFinite(btcPrice) && (
        <div className="mt-4 p-3 rounded-lg bg-orange-500/5 border border-orange-500/20">
          <div className="flex items-start gap-2">
            <Bitcoin className="w-4 h-4 text-orange-400 mt-0.5 flex-shrink-0" />
            <div className="text-xs text-slate-300 leading-relaxed">
              <span className="text-orange-300 font-semibold">BTC connection:</span>{' '}
              At <strong className="text-sky-300 tabular-nums">{simulatedYield.toFixed(2)}%</strong>{' '}
              "risk-free", BTC needs to{' '}
              {simulatedYield > currentYield
                ? <>outpace a <em>higher</em> hurdle to look attractive</>
                : simulatedYield < currentYield
                ? <>only beat a <em>lower</em> hurdle — easier to justify holding</>
                : <>match the current hurdle that's already priced in</>}
              . Today's BTC at{' '}
              <strong className="text-orange-300 tabular-nums">${Math.round(btcPrice).toLocaleString()}</strong>{' '}
              competes for the same dollars as Treasuries paying that yield.
            </div>
          </div>
        </div>
      )}

      <p className="text-[11px] text-slate-500 italic mt-3 leading-relaxed">
        Educational approximations only. Borrowing-cost projections drive
        everything from the 10Y slider:{' '}
        <strong className="not-italic text-slate-400">mortgage</strong> uses 10Y + 2pp,{' '}
        <strong className="not-italic text-slate-400">auto loan</strong> uses 10Y + 4pp,{' '}
        <strong className="not-italic text-slate-400">credit card</strong> uses 10Y + 20pp
        (a rough chain via Prime ≈ FFR + 3pp and CC margin ≈ Prime + 18pp; CC APRs
        are sticky and don't move 1:1 with policy rates in real life).{' '}
        <strong className="not-italic text-slate-400">Fair P/E</strong> uses
        1 / (yield + 4% equity risk premium), a stylized inverse-yield model.
        Actual rates depend on credit, term, lender, and dozens of other factors.
      </p>

      <style>{`
        .yield-sim-slider {
          -webkit-appearance: none; appearance: none;
          width: 100%;
          height: 6px;
          border-radius: 3px;
          background: linear-gradient(to right,
            rgba(52,211,153,0.4) 0%,
            rgba(250,204,21,0.4) 50%,
            rgba(248,113,113,0.4) 100%);
          outline: none;
          /* Apple HIG / Material recommend ≥44px touch target. The visual
             thumb stays small (18px) but a transparent extended hit zone
             gives fingers a generous target. */
          padding: 14px 0;            /* expands hit area to 6px + 28px = 34px */
          margin: -14px 0;            /* offset so layout doesn't shift */
        }
        /* Visible keyboard focus indicator — required for a11y; the default
           browser ring is invisible on the inline gradient track. */
        .yield-sim-slider:focus-visible {
          box-shadow: 0 0 0 2px rgba(56,189,248,0.7);
          border-radius: 3px;
        }
        .yield-sim-slider::-webkit-slider-thumb {
          -webkit-appearance: none; appearance: none;
          width: 22px; height: 22px;
          border-radius: 50%;
          background: #38bdf8;
          border: 3px solid #0c1322;
          box-shadow: 0 0 10px rgba(56,189,248,0.5);
          cursor: pointer;
          transition: transform 0.15s, box-shadow 0.15s;
        }
        .yield-sim-slider::-webkit-slider-thumb:hover,
        .yield-sim-slider:focus-visible::-webkit-slider-thumb {
          transform: scale(1.15);
          box-shadow: 0 0 16px rgba(56,189,248,0.85);
        }
        .yield-sim-slider::-moz-range-thumb {
          width: 22px; height: 22px;
          border-radius: 50%;
          background: #38bdf8;
          border: 3px solid #0c1322;
          box-shadow: 0 0 10px rgba(56,189,248,0.5);
          cursor: pointer;
          transition: transform 0.15s, box-shadow 0.15s;
        }
        .yield-sim-slider::-moz-range-thumb:hover,
        .yield-sim-slider:focus-visible::-moz-range-thumb {
          transform: scale(1.15);
          box-shadow: 0 0 16px rgba(56,189,248,0.85);
        }
        /* Touch devices — bump thumb past 44px guideline */
        @media (pointer: coarse) {
          .yield-sim-slider::-webkit-slider-thumb { width: 28px; height: 28px; }
          .yield-sim-slider::-moz-range-thumb     { width: 28px; height: 28px; }
        }
      `}</style>
    </div>
  );
}

// Custom tile for the equities slot — shows the model's "fair P/E" alongside
// the *actual* trailing 12-month S&P 500 P/E from multpl, plus a valuation
// readout (overvalued / fair / undervalued). The standard ImpactTile pattern
// (one big number) loses the comparison this widget exists to convey.
function PETile({ fairPE, actualPE, deltaFromBaseline }) {
  const hasActual = Number.isFinite(actualPE);
  // Valuation read: if actual is much higher than fair, equities are
  // expensive; if much lower, cheap. Threshold of ±15% is a reasonable
  // band — markets routinely deviate by single-digit % from fair value.
  const ratio = hasActual ? actualPE / fairPE : null;
  const verdict = !hasActual ? null
    : ratio > 1.15 ? { label: 'Overvalued',  tone: 'text-red-300',     icon: '↑' }
    : ratio < 0.85 ? { label: 'Undervalued', tone: 'text-emerald-300', icon: '↓' }
    :                { label: 'Near fair',   tone: 'text-amber-300',   icon: '≈' };

  // Delta from baseline (slider movement) — same sentiment logic as
  // ImpactTile: P/E rising = good for stockholders.
  const isFlat = Math.abs(deltaFromBaseline) < 0.05;
  const deltaTone = isFlat ? 'text-slate-500'
    : deltaFromBaseline > 0 ? 'text-emerald-300'
    : 'text-red-300';
  const deltaSign = deltaFromBaseline > 0 ? '+' : '';

  return (
    <div className="px-4 py-3 bg-slate-950/50 border border-white/10 rounded-lg">
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-slate-400">
        <BarChart3 className="w-3 h-3" style={{ color: '#a78bfa' }} />
        S&P 500 P/E
      </div>
      {/* Side-by-side: live actual P/E and the model's fair P/E. The model
          number shifts as the user drags the slider; the actual stays put. */}
      <div className="flex items-baseline gap-3 mt-1 flex-wrap">
        <div className="text-2xl font-bold tabular-nums text-orange-400">
          {hasActual ? `${actualPE.toFixed(1)}×` : '—'}
        </div>
        <div className="text-[10px] uppercase tracking-widest text-slate-500">actual</div>
      </div>
      <div className="flex items-baseline gap-3 mt-1.5 flex-wrap">
        <div className="text-base font-semibold tabular-nums text-sky-300">
          {fairPE.toFixed(1)}×
        </div>
        <div className="text-[10px] uppercase tracking-widest text-slate-500">model "fair"</div>
      </div>
      {verdict && (
        <div className={`text-[11px] font-semibold mt-2 ${verdict.tone}`}>
          {verdict.icon} {verdict.label}
          <span className="text-slate-500 font-mono font-normal ml-1.5 tabular-nums">
            {Math.round((ratio - 1) * 100) >= 0 ? '+' : ''}{Math.round((ratio - 1) * 100)}% vs fair
          </span>
        </div>
      )}
      <div className={`text-[11px] font-mono tabular-nums mt-1 ${deltaTone}`}>
        {isFlat
          ? 'fair: at today'
          : `fair: ${deltaSign}${deltaFromBaseline.toFixed(1)}× vs today`}
      </div>
    </div>
  );
}

function ImpactTile({ icon: Icon, iconColor, label, headline, subtitle, delta, deltaUnit, goodWhen }) {
  // Delta color = sentiment, not just sign:
  //   - For metrics where rising is good (Treasury income, equity P/E):
  //       delta > 0 → green (you gain)
  //       delta < 0 → red   (you lose)
  //   - For metrics where rising is bad (mortgage cost):
  //       delta > 0 → red   (cost up = pain)
  //       delta < 0 → green (cost down = relief)
  const isFlat = Math.abs(delta) < 0.005;
  const isGain = goodWhen === 'rises' ? delta > 0 : delta < 0;
  const deltaTone = isFlat
    ? 'text-slate-500'
    : isGain
      ? 'text-emerald-300'
      : 'text-red-300';
  const deltaSign = delta > 0 ? '+' : '';
  return (
    <div className="px-4 py-3 bg-slate-950/50 border border-white/10 rounded-lg">
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-slate-400">
        <Icon className="w-3 h-3" style={{ color: iconColor }} />
        {label}
      </div>
      {/* Primary value uses a single shared color (orange) across all tiles
          so the headline scale reads the same in every column. The icon's
          color carries the tile's "type" (consumer / saver / equities). */}
      <div className="text-2xl font-bold tabular-nums mt-1 text-orange-400">{headline}</div>
      <div className="text-[10px] text-slate-500 mt-0.5">{subtitle}</div>
      <div className={`text-[11px] font-mono tabular-nums mt-1 ${deltaTone}`}>
        {isFlat ? 'at today' : `${deltaSign}${formatDelta(delta, deltaUnit)} vs today`}
      </div>
    </div>
  );
}

function formatDelta(d, unit) {
  if (unit === '$/mo' || unit === '$/yr') {
    return `$${Math.abs(Math.round(d)).toLocaleString()} ${unit}`;
  }
  if (unit === '× P/E') {
    return `${Math.abs(d).toFixed(1)}×`;
  }
  return `${d.toFixed(2)} ${unit}`;
}

// ─── 3. YieldVsAssetOverlay ─────────────────────────────────────────────────
//
// Plots monthly DGS10 closes against BTC or gold price (also monthly), both
// log-normalised so they're directly comparable. Toggling the asset and the
// time window lets the user *see* the inverse correlation rather than read
// about it.

function YieldVsAssetOverlay({ dgs10, btcHistory, goldHistory }) {
  const [asset, setAsset] = useState('btc');
  const [windowYears, setWindowYears] = useState(10);

  const assetSeries = asset === 'btc' ? (btcHistory ?? []) : (goldHistory ?? []);

  const data = useMemo(() => {
    if (!dgs10.length || !assetSeries.length) return [];
    const cutoff = subYears(new Date(), windowYears);

    // Build monthly closes for DGS10 — keyed by "YYYY-MM"
    const dgsMonthly = new Map();
    for (const o of dgs10) {
      if (!isAfter(o.date, cutoff) || !Number.isFinite(o.value)) continue;
      const key = `${o.date.getFullYear()}-${String(o.date.getMonth()).padStart(2,'0')}`;
      // Keep last value of each month
      dgsMonthly.set(key, { date: o.date, value: o.value });
    }

    // Asset history is already monthly; index it by the same key
    const assetMonthly = new Map();
    for (const p of assetSeries) {
      if (!isAfter(p.date, cutoff) || !Number.isFinite(p.value)) continue;
      const key = `${p.date.getFullYear()}-${String(p.date.getMonth()).padStart(2,'0')}`;
      assetMonthly.set(key, { date: p.date, value: p.value });
    }

    // Merge on union of months (rare gaps just skip the row)
    const keys = [...new Set([...dgsMonthly.keys(), ...assetMonthly.keys()])].sort();
    const rows = keys
      .map(k => {
        const d = dgsMonthly.get(k);
        const a = assetMonthly.get(k);
        if (!d || !a) return null;
        return { ts: d.date.getTime(), date: d.date, yield10: d.value, assetPrice: a.value };
      })
      .filter(Boolean);
    return rows;
  }, [dgs10, assetSeries, windowYears]);

  if (!data.length) return null;

  const assetColor = asset === 'btc' ? '#f7931a' : '#fbbf24';
  const assetLabel = asset === 'btc' ? 'BTC price' : 'Gold price';

  return (
    <div className="rounded-xl border border-white/10 bg-slate-900/40 p-3 md:p-5">
      <div className="flex items-start justify-between gap-3 mb-3 flex-wrap">
        <div>
          <div className="text-xs font-semibold uppercase tracking-widest text-slate-300 flex items-center gap-2">
            <BarChart3 className="w-3.5 h-3.5 text-sky-400" />
            Yields vs. {asset === 'btc' ? 'Bitcoin' : 'Gold'} (last {windowYears}Y)
          </div>
          <p className="text-[11px] text-slate-500 mt-0.5">
            See the inverse / lagged relationship play out across cycles
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <ToggleButton active={asset === 'btc'}  onClick={() => setAsset('btc')}>BTC</ToggleButton>
          <ToggleButton active={asset === 'gold'} onClick={() => setAsset('gold')}>Gold</ToggleButton>
          <span className="w-px bg-white/10" />
          {[5, 10, 20].map(y => (
            <ToggleButton
              key={y}
              active={windowYears === y}
              onClick={() => setWindowYears(y)}
            >
              {y}Y
            </ToggleButton>
          ))}
        </div>
      </div>

      <div className="h-72">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={data} margin={{ top: 6, right: 12, left: 0, bottom: 0 }}>
            <XAxis
              dataKey="ts"
              tickFormatter={(t) => format(new Date(t), 'yyyy')}
              tick={{ fill: 'rgba(255,255,255,0.5)', fontSize: 11 }}
              tickLine={false}
              minTickGap={50}
            />
            {/* Left axis — yield % */}
            <YAxis
              yAxisId="yield"
              orientation="left"
              tick={{ fill: '#7dd3fc', fontSize: 11 }}
              tickLine={false}
              width={42}
              tickFormatter={(v) => `${v.toFixed(1)}%`}
            />
            {/* Right axis — asset price (log scale so BTC's order-of-magnitude
                moves don't squash the rest of the chart) */}
            <YAxis
              yAxisId="asset"
              orientation="right"
              scale="log"
              domain={['dataMin', 'dataMax']}
              tick={{ fill: assetColor, fontSize: 11 }}
              tickLine={false}
              width={60}
              tickFormatter={(v) =>
                v >= 1_000_000 ? `$${(v/1_000_000).toFixed(1)}M`
                : v >= 1_000   ? `$${(v/1_000).toFixed(0)}K`
                : `$${v.toFixed(0)}`
              }
            />
            <Tooltip
              cursor={{ stroke: 'rgba(255,255,255,0.18)', strokeWidth: 1, strokeDasharray: '3 3' }}
              contentStyle={{
                background: 'rgba(20,22,26,0.96)',
                border: '1px solid rgba(255,255,255,0.1)',
                borderRadius: 8,
                fontSize: 12,
              }}
              labelFormatter={(ts) => format(new Date(ts), 'MMM yyyy')}
              formatter={(v, name) => {
                if (name === '10Y yield') return [`${v.toFixed(2)}%`, name];
                return [`$${Math.round(v).toLocaleString()}`, name];
              }}
            />
            <Legend
              wrapperStyle={{ fontSize: 11, color: '#cbd5e1' }}
              iconType="plainline"
            />
            <Line
              yAxisId="yield" dataKey="yield10" name="10Y yield"
              stroke="#38bdf8" strokeWidth={1.8} dot={false} isAnimationActive={false}
            />
            <Line
              yAxisId="asset" dataKey="assetPrice" name={assetLabel}
              stroke={assetColor} strokeWidth={1.8} dot={false} isAnimationActive={false}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>

      <p className="text-[10px] text-slate-500 italic mt-2">
        Asset prices on log scale (right axis) so order-of-magnitude moves stay readable.
        Watch for periods where rising yields (blue ↑) coincide with falling asset prices
        ({asset === 'btc' ? 'orange' : 'amber'} ↓), and vice versa.
      </p>
    </div>
  );
}

function ToggleButton({ active, onClick, children }) {
  return (
    <button
      onClick={onClick}
      className={`text-[11px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-md border transition-colors ${
        active
          ? 'bg-sky-500/15 border-sky-500/40 text-sky-300'
          : 'bg-slate-900/40 border-white/10 text-slate-400 hover:bg-slate-900/60'
      }`}
    >
      {children}
    </button>
  );
}

// ─── Header / common bits ───────────────────────────────────────────────────

function YieldTile({ label, subtitle, value, trend, delta, color, asOf }) {
  const Icon = trend === 'up' ? TrendingUp : trend === 'down' ? TrendingDown : Activity;
  // 30-day delta as a badge directly below the headline so it reads as
  // "this is the 4.59% number, and here's how it's moved." Color carries
  // sentiment for a saver / consumer perspective: rising yields = orange
  // (cost going up), falling = green (relief). It's a wash for users who
  // hold bonds vs those who borrow against them, but on balance the
  // typical retail user is a borrower, so this leans into that framing.
  const trendBadgeBg = trend === 'up'   ? 'bg-orange-500/10 border-orange-500/30 text-orange-300'
                     : trend === 'down' ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-300'
                     :                    'bg-slate-700/30 border-slate-600/30 text-slate-400';
  return (
    <div className="px-4 py-3 bg-slate-900/40 border border-white/10 rounded-lg">
      <div className="flex items-center justify-between gap-2 mb-1">
        <div className="text-[10px] uppercase tracking-widest text-slate-500">{label}</div>
        {asOf && (
          <div className="text-[10px] text-slate-500 font-mono tabular-nums">
            {format(asOf, 'MMM d')}
          </div>
        )}
      </div>
      <div className="text-3xl font-bold tabular-nums" style={{ color }}>{value}</div>
      <div className="flex items-center gap-2 mt-1.5 flex-wrap">
        <div className="text-[11px] text-slate-500">{subtitle}</div>
        {delta != null && (
          <span
            className={`inline-flex items-center gap-1 text-[10px] font-mono tabular-nums px-1.5 py-0.5 rounded border ${trendBadgeBg}`}
            title="Change in 10-Year yield over the last 30 days"
          >
            <Icon className="w-3 h-3" />
            <span>{delta >= 0 ? '+' : ''}{delta.toFixed(2)}pp · 30d</span>
          </span>
        )}
      </div>
    </div>
  );
}

function SpreadTile({ label, subtitle, value, inverted, asOf }) {
  const tone = inverted
    ? { val: 'text-red-400',     ring: 'border-red-500/30',     bg: 'bg-red-500/5' }
    : { val: 'text-emerald-400', ring: 'border-emerald-500/20', bg: 'bg-emerald-500/5' };
  return (
    <div className={`px-4 py-3 border rounded-lg ${tone.ring} ${tone.bg}`}>
      <div className="flex items-center justify-between gap-2 mb-1">
        <div className="text-[10px] uppercase tracking-widest text-slate-500">{label}</div>
        <div className="text-[10px] text-slate-500 font-mono tabular-nums">
          {format(asOf, 'MMM d, yyyy')}
        </div>
      </div>
      <div className={`text-3xl font-bold tabular-nums ${tone.val}`}>{value}</div>
      <div className="text-[11px] text-slate-500 mt-1.5">
        {subtitle} · {inverted ? 'inverted (recession signal)' : 'normal (expansion)'}
      </div>
    </div>
  );
}

function RecentTrendChart({ data, latest }) {
  return (
    <div className="h-36">
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={data} margin={{ top: 6, right: 6, left: 0, bottom: 4 }}>
          <defs>
            <linearGradient id="bondYieldGrad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%"   stopColor="#38bdf8" stopOpacity={0.35} />
              <stop offset="100%" stopColor="#38bdf8" stopOpacity={0.02} />
            </linearGradient>
          </defs>
          <XAxis
            dataKey="ts"
            type="number"
            scale="time"
            domain={['dataMin', 'dataMax']}
            tick={{ fill: 'rgba(255,255,255,0.45)', fontSize: 10 }}
            tickFormatter={(t) => format(new Date(t), 'MMM yyyy')}
            tickLine={false}
            axisLine={false}
            minTickGap={50}
          />
          <YAxis
            domain={['dataMin - 0.2', 'dataMax + 0.2']}
            tick={{ fill: 'rgba(255,255,255,0.4)', fontSize: 10 }}
            tickFormatter={(v) => `${v.toFixed(1)}%`}
            tickLine={false}
            axisLine={false}
            width={42}
          />
          <ReferenceLine y={latest} stroke="rgba(56,189,248,0.4)" strokeDasharray="3 3" />
          <Tooltip
            cursor={{ stroke: 'rgba(255,255,255,0.18)', strokeWidth: 1, strokeDasharray: '3 3' }}
            contentStyle={{
              background: 'rgba(20,22,26,0.96)',
              border: '1px solid rgba(255,255,255,0.1)',
              borderRadius: 8,
              fontSize: 12,
            }}
            labelFormatter={(ts) => format(new Date(ts), 'EEE, MMM d, yyyy')}
            formatter={(v) => [`${v.toFixed(2)}%`, '10Y yield']}
          />
          <Area type="monotone" dataKey="value" stroke="#38bdf8" strokeWidth={1.5}
                fill="url(#bondYieldGrad)" isAnimationActive={false} />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}

function InversionBanner() {
  return (
    <div className="rounded-lg border border-amber-500/30 bg-amber-500/5 p-3 flex items-start gap-2">
      <AlertTriangle className="w-4 h-4 text-amber-400 mt-0.5 flex-shrink-0" />
      <div className="text-xs text-amber-200/90 leading-relaxed">
        <span className="font-semibold text-amber-300">Yield curve is inverted</span>{' '}
        (2-year yields higher than 10-year). Historically, an inversion has preceded
        every US recession in the last 50 years, typically within 12-18 months.
      </div>
    </div>
  );
}

function DefinitionsFootnote() {
  return (
    <div className="rounded-lg border border-white/5 bg-slate-900/30 p-4 text-xs text-slate-400 leading-relaxed space-y-2">
      <p>
        <strong className="text-slate-200">Bond yield:</strong> the annualised return
        an investor earns from a bond. When bond prices rise, yields fall (and vice
        versa) — they're two sides of the same trade.
      </p>
      <p>
        <strong className="text-slate-200">Curve inversion:</strong> when short-term
        Treasury yields exceed long-term yields, signalling that markets expect future
        rate cuts (usually because of an expected economic slowdown).
      </p>
      <p>
        <strong className="text-slate-200">Real-world impact:</strong> the 10Y yield
        anchors mortgage rates, corporate bond costs, and the discount rate equity
        analysts use. Higher yields = tighter conditions everywhere.
      </p>
    </div>
  );
}

function Skeleton() {
  return (
    <section className="rounded-2xl border border-white/10 bg-slate-900/60 p-6 animate-pulse">
      <div className="flex items-center gap-2 mb-3">
        <div className="w-5 h-5 rounded bg-slate-800" />
        <div className="h-5 w-56 rounded bg-slate-800" />
      </div>
      <div className="grid grid-cols-2 gap-3 mt-3">
        <div className="h-20 rounded bg-slate-800" />
        <div className="h-20 rounded bg-slate-800" />
      </div>
      <div className="h-32 rounded bg-slate-800 mt-4" />
      <div className="h-24 rounded bg-slate-800 mt-4" />
      <div className="h-48 rounded bg-slate-800 mt-4" />
    </section>
  );
}

// ─── Pure helpers ───────────────────────────────────────────────────────────

function lastValid(arr) {
  if (!arr?.length) return null;
  for (let i = arr.length - 1; i >= 0; i--) {
    if (arr[i] && Number.isFinite(arr[i].value)) return arr[i];
  }
  return null;
}

function findOnOrBefore(arr, cutoff) {
  if (!arr?.length || !cutoff) return null;
  for (let i = arr.length - 1; i >= 0; i--) {
    if (arr[i]?.date && arr[i].date <= cutoff && Number.isFinite(arr[i].value)) {
      return arr[i];
    }
  }
  return null;
}

// Standard fixed-rate amortizing-loan monthly payment formula. Used for
// both the 30-year mortgage and the 60-month auto loan — same math, just
// different principal/term.
function mortgagePayment(principal, annualRatePct, years) {
  const r = annualRatePct / 100 / 12;   // monthly rate
  const n = years * 12;                  // payment count
  if (r === 0) return principal / n;
  return principal * (r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1);
}

// Credit-card minimum-payment estimate. Issuers vary, but the most common
// formula in the US is: max(1% of balance + monthly interest, $25 or $35
// floor). This produces the "minimum" line on a CC statement — pay only
// this and the balance will take 20+ years to clear, hence the simulator
// surfaces it as a "what you'd at least owe each month" anchor.
function creditCardMinPayment(balance, annualRatePct) {
  const monthlyInterest = balance * (annualRatePct / 100 / 12);
  const principalPortion = balance * 0.01;
  return Math.max(monthlyInterest + principalPortion, 25);
}
